import React, { useRef, useEffect, useState, useMemo } from "react";
import { MapContainer, Marker, TileLayer, Tooltip, FeatureGroup, useMap, Polygon } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import Toast from "components/Toast";
import "leaflet-imageoverlay-rotated";
import yellowCircle from "../../assets/markers/yellowCircle.png";
import AntPathLayer from "./AntPathLayer";
import SpacesService from '../../api/spaces.service';
import TenantService from "../../api/tenant.service";

// Fix for missing default marker icons in Leaflet
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
    iconRetinaUrl: require("leaflet/dist/images/marker-icon-2x.png"),
    iconUrl: require("leaflet/dist/images/marker-icon.png"),
    shadowUrl: require("leaflet/dist/images/marker-shadow.png"),
});

const ResizeMap = () => {
    const map = useMap();
    useEffect(() => {
        const timer = setTimeout(() => {
            map.invalidateSize();
        }, 300);
        return () => clearTimeout(timer);
    }, [map]);
    return null;
};

const ZoomToPoint = ({ lat, lng }) => {
    const map = useMap();
    useEffect(() => {
        if (lat && lng) {
            map.setView([lat, lng], 18);
        }
    }, [lat, lng, map]);
    return null;
};

const FitBounds = ({ bounds }) => {
    const map = useMap();
    useEffect(() => {
        if (bounds && bounds.isValid()) {
            map.fitBounds(bounds, { padding: [20, 20], maxZoom: 15 });
        }
    }, [map, bounds]);
    return null;
};

export default function FloorplanPopup({ data, highlightRow, selectedRow }) {
    const listRef = useRef();
    const markerRef = useRef(null);
    const mapRef = useRef(null);
    const tenant = TenantService.getTenant();
    
    const [floorplanData, setFloorplanData] = useState([]);
    const [palette, setPalette] = useState([]);
    const [toastConfig, setToastConfig] = useState({
        isVisible: false,
        type: "info",
        message: "",
        displayTime: 5000,
    });

    useEffect(() => {
        getFloorplanImages();
        // Fetch the shared color palette
        fetch('/colors.json')
            .then(res => res.json())
            .then(data => setPalette(data))
            .catch(err => console.error("Error loading colors:", err));
    }, []);

    // Process data into colored groups (Polylines + Markers)
    const processedData = useMemo(() => {
        if (!data || !Array.isArray(data) || palette.length === 0) {
            return { groups: [], allMarkers: [], bounds: L.latLngBounds([[50, -1], [52, 1]]) };
        }

        const groups = {};
        const assetColors = {};
        let colorIdx = 0;

        const getRGB = (idx) => {
            const c = palette[idx % palette.length];
            return `rgb(${c[0]}, ${c[1]}, ${c[2]})`;
        };

        const filtered = data
            .filter(item => item.geo_coordinates && typeof item.geo_coordinates.x === 'number')
            .sort((a, b) => new Date(a.time) - new Date(b.time));

        const coordsForBounds = [];

        filtered.forEach(item => {
            const name = item.asset_name || item.asset || "Unknown Asset";
            
            if (!assetColors[name]) {
                assetColors[name] = getRGB(colorIdx);
                colorIdx++;
            }

            const color = assetColors[name];
            const pos = [item.geo_coordinates.x, item.geo_coordinates.y];
            coordsForBounds.push(pos);

            if (!groups[name]) {
                groups[name] = { name, color, points: [] };
            }
            groups[name].points.push(pos);
        });

        return {
            groups: Object.values(groups),
            allMarkers: filtered.map(item => ({
                ...item,
                pos: [item.geo_coordinates.x, item.geo_coordinates.y],
                color: assetColors[item.asset_name || item.asset]
            })),
            bounds: coordsForBounds.length > 0 ? L.latLngBounds(coordsForBounds) : L.latLngBounds([[50, -1], [52, 1]])
        };
    }, [data, palette]);

    function anchorMarkers() {
        // Creates a colored circle marker dynamically using CSS
        const createDynamicIcon = (color) => L.divIcon({
            className: 'custom-asset-marker',
            html: `<div style="background-color: ${color}; width: 12px; height: 12px; border-radius: 50%; border: 2px solid white; box-shadow: 0 0 4px rgba(0,0,0,0.4);"></div>`,
            iconSize: [12, 12],
            iconAnchor: [6, 6]
        });

        return (
            <FeatureGroup ref={markerRef}>
                {/* Path for each asset */}
                {processedData.groups.map(group => (
                    <AntPathLayer 
                        key={`path-${group.name}`} 
                        positions={group.points} 
                        options={{ color: group.color, weight: 3, paused: false, delay: 1000 }} 
                    />
                ))}

                {/* Individual markers for each point */}
                {processedData.allMarkers.map((marker, i) => (
                    <Marker 
                        key={`${marker.time}-${i}`} 
                        icon={createDynamicIcon(marker.color)} 
                        position={marker.pos}
                    >
                        <Tooltip direction="top" offset={[0, -5]} opacity={1}>
                            <strong>{marker.asset_name || marker.asset}</strong><br/>
                            {new Date(marker.time).toLocaleString()}
                        </Tooltip>
                    </Marker>
                ))}
            </FeatureGroup>
        );
    }

    function getFloorplanImages() {
        SpacesService.getSpaces(tenant).then((res) => {
            let resData = res.data.data || [];
            const images = resData
                .filter(item => item.geoCoordinates !== null)
                .map(item => {
                    const jsonCompatibleStr = item.geoCoordinates.replace(/\(/g, '[').replace(/\)/g, ']');
                    return {
                        id: item.id,
                        geo: JSON.parse(jsonCompatibleStr),
                        name: item.name
                    };
                });
            setFloorplanData(images);
        });
    }

    function getImages() {
        return (
            <>
                {floorplanData.map((image, i) => (
                    <React.Fragment key={`space-${image.id}-${i}`}>
                        <Polygon 
                            positions={image.geo} 
                            pathOptions={{ fillColor: '#B51716', color: '#B51716', fillOpacity: 0.1, weight: 1 }} 
                        />
                        <Marker position={image.geo[0]} opacity={0}>
                            <Tooltip direction="top" permanent opacity={0.7}>
                                {image.name}
                            </Tooltip>
                        </Marker>
                    </React.Fragment>
                ))}
            </>
        );
    }

    function showHovered() {
        const highlightIcon = new L.icon({
            iconUrl: yellowCircle,
            iconSize: [14, 14],
            iconAnchor: [7, 7]
        });

        if (selectedRow?.geo_coordinates) {
            const pos = [selectedRow.geo_coordinates.x, selectedRow.geo_coordinates.y];
            return (
                <Marker position={pos} icon={highlightIcon} zIndexOffset={1000}>
                    <Tooltip direction="top" permanent>Selected Position</Tooltip>
                </Marker>
            );
        }
        return null;
    }

    if (data.length === 0) return null;

    return (
        <>
            <Toast setParentToastConfig={setToastConfig} toastBody={toastConfig} />
            <div style={{ height: "100%", width: "100%", position: 'relative' }} ref={listRef}>
                <MapContainer
                    id="map"
                    bounds={processedData.bounds}
                    style={{ height: "100%", width: "100%" }}
                    whenReady={(mapInstance) => { mapRef.current = mapInstance.target; }}
                >
                    <ResizeMap />
                    <TileLayer
                        url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                        attribution="&copy; OpenStreetMap"
                        maxZoom={19}
                    />
                    
                    {getImages()}
                    {anchorMarkers()}
                    {showHovered()}
                    
                    <FitBounds bounds={processedData.bounds} />
                    
                    {selectedRow?.geo_coordinates && (
                        <ZoomToPoint 
                            lat={selectedRow.geo_coordinates.x} 
                            lng={selectedRow.geo_coordinates.y} 
                        />
                    )}
                </MapContainer>

                {/* Optional: Legend overlay */}
                <div style={{ 
                    position: 'absolute', bottom: 20, right: 20, zIndex: 1000, 
                    background: 'white', padding: '10px', borderRadius: '5px', 
                    boxShadow: '0 0 5px rgba(0,0,0,0.3)', maxHeight: '200px', overflowY: 'auto' 
                }}>
                    <h4 style={{ margin: '0 0 5px 0', fontSize: '12px' }}>Assets</h4>
                    {processedData.groups.map(g => (
                        <div key={g.name} style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '11px' }}>
                            <div style={{ width: '10px', height: '10px', background: g.color, borderRadius: '50%' }}></div>
                            {g.name}
                        </div>
                    ))}
                </div>
            </div>
        </>
    );
}